import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useDrive } from "../context/DriveContext";
import DriveRequired from "../components/DriveRequired";
import FileMappingDialog from "../components/FileMappingDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { ArrowsClockwise, FileText, PencilSimple, Trash, CheckCircle, WarningCircle, FolderOpen } from "@phosphor-icons/react";

const FMT_BADGE = {
  xls: { label: "XLS", bg: "bg-[#e7e7e4]", text: "text-[#0a0a0a]" },
  xlsx: { label: "XLSX", bg: "bg-[#e7e7e4]", text: "text-[#0a0a0a]" },
  csv: { label: "CSV", bg: "bg-[#e7e7e4]", text: "text-[#0a0a0a]" },
};

export default function FilesPage() {
  const { connected, checking } = useDrive();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [activeFile, setActiveFile] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/files");
      setFiles(data);
    } catch (e) {
      console.error("load files failed", e);
      toast.error("Не удалось загрузить список файлов");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (connected) load();
    else setLoading(false);
  }, [connected, load]);

  const grouped = useMemo(() => {
    const by = {};
    for (const f of files) {
      const k = f.folder_name || "Без папки";
      (by[k] = by[k] || []).push(f);
    }
    return Object.entries(by).sort(([a], [b]) => a.localeCompare(b));
  }, [files]);

  if (checking) {
    return (
      <div className="p-6 lg:p-10">
        <div className="mono-label">ЗАГРУЗКА...</div>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="p-6 lg:p-10 max-w-4xl">
        <div className="mono-label mb-2">СЕКЦИЯ / ПРАЙСЫ</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Прайс-листы</h1>
        <p className="text-sm text-[#52525b] mt-2 max-w-2xl">
          Реестр работает с файлами на Google Drive — нужен доступ.
        </p>
        <div className="mt-6">
          <DriveRequired />
        </div>
      </div>
    );
  }

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/files/scan");
      toast.success(`Папок: ${data.folders}, добавлено ${data.added}, обновлено ${data.updated}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ошибка сканирования");
    } finally {
      setScanning(false);
    }
  };

  const del = (id, fileName) => {
    setConfirm({
      title: "Удалить файл из реестра?",
      description: `${fileName ? `«${fileName}». ` : ""}На Google Drive файл останется.`,
      confirmLabel: "Удалить",
      tone: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/files/${id}`);
          toast.success("Удалён из реестра");
          await load();
        } catch (e) {
          console.error("delete file failed", e);
          toast.error("Ошибка удаления");
        }
      },
    });
  };

  const mappedCount = files.filter((f) => f.mapped).length;

  return (
    <div className="p-6 lg:p-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label mb-2">SECTION / PRICE FILES</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Прайс-листы</h1>
          <p className="text-sm text-[#52525b] mt-2 max-w-2xl">
            Список обнаруженных файлов. Настройте маппинг столбцов, чтобы при добавлении
            строки данные попадали в правильные колонки каждого файла.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="mono-label">ЗАМАПЛЕНО</div>
            <div className="font-mono text-xl font-semibold">
              {mappedCount} <span className="text-[#52525b]">/ {files.length}</span>
            </div>
          </div>
          <button
            onClick={scan}
            disabled={scanning}
            data-testid="files-scan-button"
            className="inline-flex items-center gap-2 border border-[#18181b] bg-transparent text-[#18181b] px-4 py-2 text-sm hover:bg-[#18181b] hover:text-white transition-colors duration-150 disabled:opacity-50"
          >
            <ArrowsClockwise size={14} weight="bold" className={scanning ? "animate-spin" : ""} />
            {scanning ? "Сканирование..." : "Обновить с Drive"}
          </button>
        </div>
      </div>

      {loading && (
        <div className="mt-8 mono-label">ЗАГРУЗКА...</div>
      )}

      {!loading && files.length === 0 && (
        <div className="mt-8 bg-white border border-[#d4d4d8] p-8 text-center" data-testid="files-empty">
          <FolderOpen size={32} weight="duotone" className="mx-auto text-[#52525b]" />
          <h3 className="text-lg font-medium mt-3">Файлов пока нет</h3>
          <p className="text-sm text-[#52525b] mt-1">
            Убедитесь, что в «Настройках» указана корневая папка, и нажмите «Обновить с Drive».
          </p>
        </div>
      )}

      <div className="mt-8 space-y-8">
        {grouped.map(([folder, folderFiles]) => (
          <section key={folder} data-testid={`folder-group-${folder}`}>
            <div className="flex items-center gap-3 mb-3">
              <FolderOpen size={18} weight="duotone" className="text-[#0047FF]" />
              <h2 className="text-lg font-medium tracking-tight">{folder}</h2>
              <span className="mono-label">{folderFiles.length} ФАЙЛОВ</span>
            </div>

            <div className="border border-[#d4d4d8] bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#d4d4d8] bg-[#f5f5f4]">
                    <th className="text-left px-4 py-2 mono-label">Файл</th>
                    <th className="text-left px-4 py-2 mono-label">Тип</th>
                    <th className="text-left px-4 py-2 mono-label">Маппинг</th>
                    <th className="text-left px-4 py-2 mono-label">Столбцов</th>
                    <th className="text-right px-4 py-2 mono-label">Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {folderFiles.map((f) => {
                    const fmt = FMT_BADGE[f.fmt] || { label: f.fmt, bg: "bg-[#e7e7e4]", text: "text-[#0a0a0a]" };
                    return (
                      <tr
                        key={f.id}
                        data-testid={`file-row-${f.id}`}
                        className="border-b border-[#d4d4d8] last:border-b-0 hover:bg-[#f5f5f4] transition-colors duration-150"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <FileText size={16} weight="regular" className="text-[#52525b]" />
                            <span className="font-medium">{f.file_name}</span>
                            {f.subpath && (
                              <span className="mono-label text-[#52525b]">/ {f.subpath}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-[10px] px-1.5 py-0.5 ${fmt.bg} ${fmt.text} border border-[#d4d4d8]`}>
                            {fmt.label}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-testid="file-status-badge">
                          {f.mapped ? (
                            <span className="inline-flex items-center gap-1.5 text-[#00994C]">
                              <CheckCircle size={14} weight="fill" />
                              <span className="text-xs font-medium">Готов</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-[#FF2A00]">
                              <WarningCircle size={14} weight="fill" />
                              <span className="text-xs font-medium">Нужен маппинг</span>
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[#52525b]">
                          {f.total_cols ?? "—"}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setActiveFile(f)}
                              data-testid={`file-mapping-edit-${f.id}`}
                              className="inline-flex items-center gap-1 text-xs border border-[#18181b] px-2.5 py-1.5 hover:bg-[#18181b] hover:text-white transition-colors duration-150"
                            >
                              <PencilSimple size={12} weight="bold" />
                              Маппинг
                            </button>
                            <button
                              onClick={() => del(f.id, f.file_name)}
                              data-testid={`file-delete-${f.id}`}
                              title="Удалить из реестра"
                              className="p-1.5 text-[#52525b] hover:text-[#FF2A00] hover:bg-[#f5f5f4] transition-colors duration-150"
                            >
                              <Trash size={14} weight="regular" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ))}
      </div>

      {activeFile && (
        <FileMappingDialog
          file={activeFile}
          onClose={() => setActiveFile(null)}
          onSaved={async () => {
            setActiveFile(null);
            await load();
          }}
        />
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
